import {
    __private,
    assetManager,
    ImageAsset,
    macro,
    Texture2D,
    director,
    Scene,
    Director,
    dynamicAtlasManager,
    AssetManager,
    sys,
    SpriteFrame,
    Sprite,
    BitmapFont,
    Label,
    gfx,
    TextureBase,
} from 'cc';

import { BUILD, DEBUG } from 'cc/env';
import Parameter from './Toolkit/Utils/Parameter';
import { executeActionPerFrame } from './Toolkit/Utils/Utility';

type LetterDefinition = {
    u: number;
    v: number;
    w: number;
    h: number;
    offsetX: number;
    offsetY: number;
    textureID: number;
    valid: boolean;
    xAdvance: number;
};

type Config = {
    isEnable: boolean;
    isResize: boolean;
    loadHighQuality: boolean;
    loadHighQualityOptions?: __private._cocos_core_asset_manager_shared__IOptions;
};

type UseProgressiveCache = {
    enabled: Record<string, Record<string, boolean>>;
    plist: Record<string, string>;
    pac: Record<string, string>;
    downscaleRatio: number;
};

/**
 * 漸進式加載管理器
 *
 * 為了加速初次場景啟動，於載入流程中先攔截可支援的貼圖為低畫質版本，
 * 待場景啟動完成後再非同步補載高畫質貼圖並恢復相關資源狀態。
 */
export class ProgressiveLoadingManager {
    private static _assetConfigFileName = 'progressive-map.json';
    private static _lowQualityFileName = '.resize.low';
    private static _resizeScale = 4;
    /** 配合 cocos-build 支援的圖片格式 */
    private static readonly SUPPORTED_IMAGE_FORMATS = ['.webp', '.astc', '.avif'];
    private static readonly LOW_QUALITY_PIPELINE_INDEX = 1;

    private _config: Config = null;
    private _loadedAssetUrls: Set<string> = new Set();
    private _assetProgressiveCache: UseProgressiveCache = null;
    private _assetProgressiveCachePromise = null;
    private _originalPackable: Map<SpriteFrame, boolean> = new Map();
    private _isAstcSupported = false;
    private _isWebpSupported = false;
    private _isAVIFSupported = false;

    constructor() {
        this._initConfig();
        if (!this._config.isEnable || !BUILD || !macro.ENABLE_PROGRESSIVE_LOADING) {
            return;
        }
        this._initialize();
    }

    private _initConfig() {
        const astcFeatures = director.root.device.getFormatFeatures(gfx.Format.ASTC_RGBA_8X8);
        this._isAstcSupported = (astcFeatures & gfx.FormatFeatureBit.SAMPLED_TEXTURE) !== 0;
        this._isWebpSupported = sys.hasFeature(sys.Feature.WEBP);
        this._isAVIFSupported = sys.hasFeature(sys.Feature.AVIF);

        this._config = {
            isEnable: DEBUG ? Parameter.get('loadLowQuality', 'true') === 'true' : true,
            isResize: DEBUG ? Parameter.get('isResize', 'true') === 'true' : true,
            loadHighQuality: DEBUG ? Parameter.get('loadHighQuality', 'true') === 'true' : true,
            loadHighQualityOptions: {
                maxConcurrency: 1,
                maxRequestsPerFrame: 1,
            },
        };

        if (this._config.isResize) {
            ProgressiveLoadingManager._lowQualityFileName = `.resize.low`;
            ProgressiveLoadingManager._resizeScale = 4;
        } else {
            ProgressiveLoadingManager._lowQualityFileName = `.low`;
            ProgressiveLoadingManager._resizeScale = 1;
        }
    }

    private _initialize(): void {
        this._assetProgressiveCachePromise = fetch(ProgressiveLoadingManager._assetConfigFileName);
        assetManager.pipeline.insert(this._changeUrlToLowQuality.bind(this), ProgressiveLoadingManager.LOW_QUALITY_PIPELINE_INDEX);
        assetManager.fetchPipeline.insert(this._changeUrlToLowQuality.bind(this), ProgressiveLoadingManager.LOW_QUALITY_PIPELINE_INDEX);

        this._patchSpriteApplySpriteSize();
        this._patchSpriteRefreshTexture();
        this._patchLabelOnLoaded();
        this._patchBitmapFontOnLoaded();

        // 場景啟動後載入高畫質圖片
        if (this._config.loadHighQuality) {
            director.on(Director.EVENT_AFTER_SCENE_LAUNCH, this._onSceneLaunchedHandler, this);
        }
    }

    /**
     * 攔截載入 URL 並替換為低畫質版本
     * @param task 資源載入任務
     * @param done pipeline 完成回調
     */
    private async _changeUrlToLowQuality(task: AssetManager.Task, done: Function) {
        const data = await this._assetProgressiveCachePromise;
        if (!this._assetProgressiveCache) {
            this._assetProgressiveCache = await data.json();
            if (this._config.isResize) {
                ProgressiveLoadingManager._resizeScale = 1 / this._assetProgressiveCache.downscaleRatio;
            } else {
                ProgressiveLoadingManager._resizeScale = 1;
            }
        }

        const input = Array.isArray(task.input) ? task.input : [task.input];
        input.forEach((item: { url: string }) => {
            if (!item?.url) {
                return;
            }
            const { url } = item;
            // 判斷是否為本地遊戲資源 URL
            if (url.includes(sys.baseUrl) || url.startsWith('assets/')) {
                const ext: string = url.slice(url.lastIndexOf('.'));
                const uuid = this._getUUID(url);
                if (this._checkEnabled(uuid) && ProgressiveLoadingManager.SUPPORTED_IMAGE_FORMATS.some((format) => format === ext)) {
                    this._loadedAssetUrls.add(item.url);
                    item.url = url.replace(/(\.\w+)$/, `${ProgressiveLoadingManager._lowQualityFileName}$1`);
                }
            }
        });
        task.output = input;
        done();
    }

    /**
     * 場景啟動後處理
     * @param changedScene 當前已啟動的場景
     */
    private _onSceneLaunchedHandler(changedScene: Scene): void {
        if (!changedScene.name.toLocaleLowerCase().includes('game')) {
            return;
        }

        // 移除攔截 pipeline 以恢復正常載入流程，並開始補載高畫質貼圖
        assetManager.pipeline.remove(ProgressiveLoadingManager.LOW_QUALITY_PIPELINE_INDEX);
        assetManager.fetchPipeline.remove(ProgressiveLoadingManager.LOW_QUALITY_PIPELINE_INDEX);

        this._loadHighQualityTextures();
    }

    /**
     * 批次補載高畫質貼圖（為何批次）：避免一次性大量同步載入造成主線程卡頓；使用受限的載入選項（低併發、低每幀請求）以平滑更新。
     */
    private _loadHighQualityTextures(): void {
        const textures = this._collectLoadedTextures();
        if (textures.length === 0) {
            return;
        }

        const urls = textures.map((asset) => ({ url: asset.image?.nativeUrl }));
        const onComplete = (
            err: Error,
            images: __private._cocos_core_assets_image_asset__ImageSource[] | __private._cocos_core_assets_image_asset__ImageSource,
        ) => {
            if (err) {
                throw err;
            }
            if (Array.isArray(images)) {
                this._processHighQualityImages(images, textures);
            } else {
                this._processHighQualityImages([images], textures);
            }
        };

        assetManager.loadAny(urls, this._config.loadHighQualityOptions, onComplete);
    }

    /**
     * 收集目前已載入但為低畫質的 Texture2D
     */
    private _collectLoadedTextures(): Texture2D[] {
        const textures: Texture2D[] = [];
        assetManager.assets.forEach((asset) => {
            if (asset instanceof Texture2D && this._isLoadedLowQualityTexture(asset)) {
                textures.push(asset);
            }
        });
        return textures;
    }

    /**
     * 判斷給定 Texture 是否為先前被攔截（低畫質）的資源。
     * @param asset 要檢查的 Texture2D
     * @returns 若該 texture 的 nativeUrl 曾被攔截則回傳 `true`
     */
    private _isLoadedLowQualityTexture(asset: Texture2D): boolean {
        const url = asset.image?.nativeUrl;
        return !!(url && this._loadedAssetUrls.has(url));
    }

    /**
     * 逐幀處理下載完成的高畫質 Image，避免一次性處理造成卡頓。
     * @param images 已下載的 Image 原生資源陣列
     * @param textures 對應要替換的 Texture2D 陣列
     */
    private _processHighQualityImages(images: __private._cocos_core_assets_image_asset__ImageSource[], textures: Texture2D[]): void {
        let index = -1;
        executeActionPerFrame(
            () => {
                index += 1;
                this._updateTextureAndRelated(images[index], textures[index]);
            },
            1,
            images.length,
        );
    }

    /**
     * 更新紋理
     */
    private _updateTexture(image: __private._cocos_core_assets_image_asset__ImageSource, url: string): void {
        const uuid = this._getUUID(url);
        if (!uuid) {
            return;
        }

        const imageAsset = assetManager.assets.get(uuid) as ImageAsset;
        const texture = assetManager.assets.find((asset) => asset.uuid.includes(uuid) && asset instanceof Texture2D) as Texture2D;

        if (imageAsset && texture) {
            const oldNativeAsset = imageAsset._nativeAsset;
            if (oldNativeAsset) {
                // 針對 Android/PC 的 ImageBitmap 釋放
                if (typeof (oldNativeAsset as any).close === 'function') {
                    (oldNativeAsset as any).close();
                }
                // 針對 iPhone 的 HTMLImageElement 釋放（切斷瀏覽器快取引用）
                else if (oldNativeAsset instanceof HTMLImageElement) {
                    oldNativeAsset.src = ''; // 強制瀏覽器釋放點陣圖記憶體
                    oldNativeAsset.onload = null;
                    oldNativeAsset.onerror = null;
                }
            }
            imageAsset!._nativeAsset = image;
            texture.image = imageAsset;

            // 參考 engine ttfUtils.ts 的釋放方式
            if (director.root && director.root.batcher2D) {
                (director.root.batcher2D as any)._releaseDescriptorSetCache(texture.getHash());
            }
        } else {
            throw new Error(`Cannot find asset for url: ${url}`);
        }
    }

    /**
     * 更新紋理與相關對應的資源
     */
    private _updateTextureAndRelated(image: __private._cocos_core_assets_image_asset__ImageSource, texture: Texture2D): void {
        if (!texture?.isValid) {
            return;
        }

        const scene = director.getScene();
        const spriteFrames = this._collectRelatedSpriteFrames(texture);
        const bitmapFonts = this._collectRelatedBitmapFonts(texture);

        this._cleanupDynamicAtlas(spriteFrames);
        this._updateTexture(image, texture.image.nativeUrl);
        spriteFrames.forEach((asset) => {
            if (this._originalPackable.has(asset)) {
                asset._checkPackable();
            }
        });

        if (director.root && director.root.batcher2D) {
            // 不釋放貼圖的 descriptorSetCache 會導致更新後仍使用舊貼圖，強制釋放以確保能正確更新(ex. spine 的 batch 模式)
            (director.root.batcher2D as any)._descriptorSetCache.destroy();
        }

        this._restoreSpriteFrameData(spriteFrames);
        this._restoreBitmapFontData(bitmapFonts);
        this._updateLabelsAndSprites(scene, texture);
    }

    /**
     * 找出所有使用該 texture 的 SpriteFrame。
     * @param texture 要查詢的 Texture2D
     * @returns 對應的 SpriteFrame 陣列
     */
    private _collectRelatedSpriteFrames(texture: Texture2D): SpriteFrame[] {
        const spriteFrames: SpriteFrame[] = [];
        assetManager.assets.forEach((asset) => {
            if (asset instanceof SpriteFrame && this._isTextureMatch(asset, texture)) {
                spriteFrames.push(asset);
            }
        });
        return spriteFrames;
    }

    /**
     * 找出所有使用該 texture 的 BitmapFont（透過其 spriteFrame）。
     * @param texture 要查詢的 Texture2D
     * @returns 對應的 BitmapFont 陣列
     */
    private _collectRelatedBitmapFonts(texture: Texture2D): BitmapFont[] {
        const bitmapFonts: BitmapFont[] = [];
        assetManager.assets.forEach((asset) => {
            if (asset instanceof BitmapFont && this._isTextureMatch(asset.spriteFrame, texture)) {
                bitmapFonts.push(asset);
            }
        });
        return bitmapFonts;
    }

    /**
     * 從動態圖集移除指定的 SpriteFrame，不然會取得錯誤的 texture
     * @param spriteFrames 要移除的 SpriteFrame 清單
     */
    private _cleanupDynamicAtlas(spriteFrames: SpriteFrame[]): void {
        spriteFrames.forEach((spriteFrame) => {
            dynamicAtlasManager.deleteAtlasSpriteFrame(spriteFrame);
            spriteFrame._resetDynamicAtlasFrame();
        });
        spriteFrames.forEach((asset) => {
            asset.isLowQuality = false;
            if (this._originalPackable.has(asset)) {
                asset.packable = this._originalPackable.get(asset);
            }
        });
    }

    /**
     * 將 SpriteFrame 的尺寸與切邊恢復為高畫質對應。
     * @param spriteFrames 要恢復的 SpriteFrame 陣列
     */
    private _restoreSpriteFrameData(spriteFrames: SpriteFrame[]): void {
        spriteFrames.forEach((asset) => {
            if (asset.rect) {
                asset.rect.x = this._scale(asset.rect.x);
                asset.rect.y = this._scale(asset.rect.y);
                asset.rect.width = this._scale(asset.rect.width);
                asset.rect.height = this._scale(asset.rect.height);
            }

            if (asset.originalSize) {
                asset.originalSize.width = this._scale(asset.originalSize.width);
                asset.originalSize.height = this._scale(asset.originalSize.height);
            }

            if (asset.offset) {
                asset.offset.x = this._scale(asset.offset.x);
                asset.offset.y = this._scale(asset.offset.y);
            }

            const capInsets = (asset as any)._capInsets;
            if (capInsets) {
                for (let i = 0; i < 4; i += 1) {
                    capInsets[i] = this._scale(capInsets[i]);
                }
            }

            // 九宮格與透明裁切是用寬高算的，因此需重新計算
            asset._calculateUV();
            (asset as any)._calcTrimmedBorder();
        });
    }

    /**
     * 將 BitmapFont 的字形定義與字體大小恢復為高畫質對應。
     * @param bitmapFonts
     */
    private _restoreBitmapFontData(bitmapFonts: BitmapFont[]): void {
        bitmapFonts.forEach((asset) => {
            const defines: Record<string, LetterDefinition> = asset.fontDefDictionary.letterDefinitions;
            Object.keys(defines).forEach((key) => {
                const def = defines[key];
                def.u = this._scale(def.u);
                def.v = this._scale(def.v);
                def.w = this._scale(def.w);
                def.h = this._scale(def.h);
                def.offsetX = this._scale(def.offsetX);
                def.offsetY = this._scale(def.offsetY);
                def.xAdvance = this._scale(def.xAdvance);
            });
            asset.fntConfig!.fontSize = this._scale(asset.fntConfig!.fontSize);
        });
    }

    /**
     * 標記場景中使用該 texture 的 Label 與 Sprite 為需要重新渲染。
     * @param scene 當前場景
     * @param texture 已更新的 Texture2D
     */
    private _updateLabelsAndSprites(scene: Scene, texture: Texture2D): void {
        const labels = scene
            .getComponentsInChildren(Label)
            .filter((label) => label?.font && label.font instanceof BitmapFont && this._isTextureMatch(label.font.spriteFrame, texture));

        labels.forEach((label) => {
            label.markForUpdateRenderData();
        });

        const sprites: Sprite[] = scene.getComponentsInChildren(Sprite).filter((sprite) => this._isTextureMatch(sprite, texture));
        sprites.forEach((sprite) => {
            (sprite as any)._applySpriteSize();
            sprite.markForUpdateRenderData();
        });
    }

    /**
     * 修補 SpriteFrame 的_refreshTexture以支援低畫質版本的資料結構調整。
     */
    private _patchSpriteRefreshTexture(): void {
        const spriteFrameRefreshTexture = (SpriteFrame.prototype as any)._refreshTexture;
        (SpriteFrame.prototype as any)._refreshTexture = function _refreshTexture(this: SpriteFrame, texture: TextureBase) {
            const uuid = progressiveLoadingManager._getUUID(texture.uuid);
            if (!this.isLowQuality && progressiveLoadingManager._checkEnabled(uuid)) {
                progressiveLoadingManager._originalPackable.set(this, this.packable);
                this.packable = false;
                this.isLowQuality = true;
                this.lowQualityResizeScale = ProgressiveLoadingManager._resizeScale;
                const scale = 1 / ProgressiveLoadingManager._resizeScale;

                if (this._rect) {
                    this._rect.x = progressiveLoadingManager._scale(this._rect.x, scale);
                    this._rect.y = progressiveLoadingManager._scale(this._rect.y, scale);
                    this._rect.width = progressiveLoadingManager._scale(this._rect.width, scale);
                    this._rect.height = progressiveLoadingManager._scale(this._rect.height, scale);
                }

                if (this._originalSize) {
                    this._originalSize.width = progressiveLoadingManager._scale(this._originalSize.width, scale);
                    this._originalSize.height = progressiveLoadingManager._scale(this._originalSize.height, scale);
                }

                if (this._offset) {
                    this._offset.x = progressiveLoadingManager._scale(this._offset.x, scale);
                    this._offset.y = progressiveLoadingManager._scale(this._offset.y, scale);
                }

                if (this._capInsets) {
                    for (let i = 0; i < 4; i += 1) {
                        this._capInsets[i] = progressiveLoadingManager._scale(this._capInsets[i], scale);
                    }
                }
            }
            spriteFrameRefreshTexture.call(this, texture);
        };
    }

    private _patchSpriteApplySpriteSize(): void {
        const spriteApplySpriteSize = (Sprite.prototype as any)._applySpriteSize;
        (Sprite.prototype as any)._applySpriteSize = function _applySpriteSize(this: Sprite) {
            if (this._spriteFrame && this._spriteFrame?.isLowQuality) {
                if (BUILD || !this._spriteFrame.isDefault) {
                    const scale = this._spriteFrame.lowQualityResizeScale;
                    if (Sprite.SizeMode.RAW === this._sizeMode) {
                        const size = this._spriteFrame.originalSize;
                        this.node._uiProps.uiTransformComp!.setContentSize(size.x * scale, size.y * scale);
                    } else if (Sprite.SizeMode.TRIMMED === this._sizeMode) {
                        const { rect } = this._spriteFrame;
                        this.node._uiProps.uiTransformComp!.setContentSize(rect.width * scale, rect.height * scale);
                    }
                }
            } else {
                spriteApplySpriteSize.call(this);
            }
        };
    }

    private _patchLabelOnLoaded(): void {
        const labelOnLoad = Label.prototype.onLoad;
        Label.prototype.onLoad = function constructor(this: Label) {
            labelOnLoad.call(this);
            // 因為 Label 的 lineHeight 會影響 SHRINK 的計算，因此需要在 onLoad 時修補 lineHeight 的 getter 與 setter
            let value = this._lineHeight;
            Object.defineProperty(this, '_lineHeight', {
                configurable: true,
                enumerable: true,
                get() {
                    if (
                        this &&
                        this.font &&
                        this.font instanceof BitmapFont &&
                        this.font.spriteFrame?.isLowQuality &&
                        this.overflow === Label.Overflow.SHRINK
                    ) {
                        return value / 2;
                    }
                    return value;
                },
                set(v: number) {
                    value = v;
                },
            });
        };
    }

    /**
     * 修補 BitmapFont 的 onLoaded 方法以支援低畫質版本的字形定義調整。
     */
    private _patchBitmapFontOnLoaded(): void {
        const bitmapFontOnLoaded = BitmapFont.prototype.onLoaded;
        BitmapFont.prototype.onLoaded = function onLoaded(this: BitmapFont) {
            bitmapFontOnLoaded.call(this);
            if (this.spriteFrame?.isLowQuality) {
                const scale = 1 / ProgressiveLoadingManager._resizeScale;
                Object.keys(this.fontDefDictionary.letterDefinitions).forEach((key) => {
                    const letter = this.fontDefDictionary.letterDefinitions[key];
                    letter.offsetX = progressiveLoadingManager._scale(letter.offsetX, scale);
                    letter.offsetY = progressiveLoadingManager._scale(letter.offsetY, scale);
                    letter.w = progressiveLoadingManager._scale(letter.w, scale);
                    letter.h = progressiveLoadingManager._scale(letter.h, scale);
                    letter.u = progressiveLoadingManager._scale(letter.u, scale);
                    letter.v = progressiveLoadingManager._scale(letter.v, scale);
                    letter.xAdvance = progressiveLoadingManager._scale(letter.xAdvance, scale);
                });
                this.fntConfig!.fontSize = progressiveLoadingManager._scale(this.fntConfig!.fontSize, scale);
                this.fontSize = progressiveLoadingManager._scale(this.fontSize, scale);
                this.fntConfig.commonHeight = progressiveLoadingManager._scale(this.fntConfig.commonHeight, scale);
            }
        };
    }

    /**
     * 檢查 SpriteFrame 或 Sprite 是否使用了指定的 Texture
     * @param source SpriteFrame 或 Sprite 實例
     * @param texture 要比對的 Texture2D 實例
     * @returns 如果 source 使用了該 texture 則返回 true，否則返回 false
     */
    private _isTextureMatch(source: SpriteFrame | Sprite, texture: Texture2D): boolean {
        const spriteFrame: SpriteFrame = source instanceof Sprite ? source.spriteFrame : source;
        const isUseDynamicAtlas = (spriteFrame as any)?._original;
        if (isUseDynamicAtlas) {
            return (spriteFrame as any)._original?._texture?.uuid.includes(texture.uuid);
        }
        return spriteFrame?.texture?.uuid.includes(texture.uuid);
    }

    private _getUUID(url: string): string | null {
        return /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{9})/i.exec(url)?.[0] ?? null;
    }

    private _scale(value: number, factor: number = ProgressiveLoadingManager._resizeScale): number {
        return value * factor;
    }

    private _convertPlistToTextureUuid(uuid: string) {
        return this._assetProgressiveCache.plist[uuid];
    }

    private _checkEnabled(uuid: string) {
        let textureUuid = uuid;
        if (this._assetProgressiveCache.plist[uuid]) {
            textureUuid = this._convertPlistToTextureUuid(uuid);
        }
        const enabled = this._assetProgressiveCache.enabled[textureUuid];
        const enabledWebp = enabled?.webp && this._isWebpSupported;
        const enabledAvif = enabled?.avif && this._isAVIFSupported;
        const enabledAstc = enabled?.astc && this._isAstcSupported;
        return enabledWebp || enabledAvif || enabledAstc;
    }
}

export const progressiveLoadingManager = new ProgressiveLoadingManager();
